// "Zeiten nachtragen" — der erzwungene Zeiterfassungs-Dialog. Geht beim
// Einloggen/Arbeiten auf, sobald ein vergangener Werktag weder einen
// Zeiteintrag noch eine Abwesenheit (Urlaub/Krank) hat. NICHT wegklickbar:
// der Mitarbeiter MUSS jeden offenen Tag klären — entweder Zeit eintragen oder
// als Urlaub/Krank markieren. Arbeitet die Tage einzeln ab (ältester zuerst).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useData } from './DataContext.jsx';
import { fmtMin, timeToMin, durationMin } from './lib/format.js';
import { formatMissingDay } from './lib/missing-times.js';
import { IconClock, IconCheck, IconPlus, IconX } from './icons.jsx';

const MEETING_VALUE = '__meeting__';

// --- Inline entry form (one missing day) -------------------------------------
function DayEntryForm({ day, onAdded }) {
  const { customers, projects, addTimeEntry } = useData();
  const [mode, setMode] = useState('project'); // 'project' | 'support'
  const [customerId, setCustomerId] = useState('');
  const [projectId, setProjectId] = useState(''); // '' | uuid | MEETING_VALUE
  const [desc, setDesc] = useState('');
  const [from, setFrom] = useState('09:00');
  const [to, setTo] = useState('17:00');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const customerProjects = useMemo(
    () => projects.filter(p => p.customer_id === customerId && (p.status === 'active' || p.status === 'closing')),
    [projects, customerId]
  );

  // Auto-pick the only project of a customer.
  useEffect(() => {
    if (mode !== 'project' || projectId === MEETING_VALUE) return;
    if (customerProjects.length === 1) setProjectId(customerProjects[0].id);
    else if (!customerProjects.find(p => p.id === projectId)) setProjectId('');
  }, [customerId, customerProjects, mode]);

  const isMeeting = projectId === MEETING_VALUE;
  const supportValid = mode === 'support' && desc.trim().length >= 3;
  const projectValid = mode === 'project' && !!projectId && !isMeeting;
  const meetingValid = mode === 'project' && isMeeting && desc.trim().length >= 3;
  const timeValid = timeToMin(to) > timeToMin(from);
  const canSave = timeValid && (supportValid || projectValid || meetingValid) && !busy;

  async function save() {
    if (!canSave) return;
    setBusy(true); setErr(null);
    try {
      const base = mode === 'support'
        ? { project_id: null, customer_id: null, kind: 'support', description: desc.trim() }
        : isMeeting
          ? { project_id: null, customer_id: customerId || null, kind: 'meeting', description: desc.trim() }
          : { project_id: projectId, customer_id: null, kind: 'project', description: desc.trim() };
      await addTimeEntry({ ...base, entry_date: day, from_time: from, to_time: to });
      // Reset for the next entry; start where this one ended.
      setDesc('');
      setFrom(to);
      setTo(timeToMin(to) + 60 <= 22 * 60 ? `${String(Math.floor(timeToMin(to) / 60) + 1).padStart(2, '0')}:00` : to);
      onAdded?.();
    } catch (e) {
      setErr(e.message || 'Fehler beim Speichern');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ border: '1px solid var(--stroke)', borderRadius: 10, padding: 12, background: 'var(--bg-elev)' }}>
      <div className="seg" style={{ width: '100%', marginBottom: 8 }}>
        <button className={mode === 'project' ? 'on' : ''} onClick={() => setMode('project')} style={{ flex: 1 }}>Projekt-Zeit</button>
        <button className={mode === 'support' ? 'on' : ''} onClick={() => setMode('support')} style={{ flex: 1 }}>Begleitaufgabe</button>
      </div>

      {mode === 'project' && (
        <>
          <select className="select" style={{ padding: '6px 8px', fontSize: 12.5, marginBottom: 6, width: '100%' }}
                  value={customerId} onChange={(e) => { setCustomerId(e.target.value); if (projectId !== MEETING_VALUE) setProjectId(''); }}>
            <option value="">Kunde wählen… (für Meeting optional)</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className="select" style={{ padding: '6px 8px', fontSize: 12.5, marginBottom: 6, width: '100%' }}
                  value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">{customerId ? 'Projekt wählen…' : 'Auswahl treffen…'}</option>
            <option value={MEETING_VALUE}>📅 Meeting</option>
            {customerId && customerProjects.length > 0 && (
              <optgroup label="Projekte">
                {customerProjects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </optgroup>
            )}
            {customerId && customerProjects.length === 0 && (
              <option disabled>— Keine aktiven Projekte für diesen Kunden —</option>
            )}
          </select>
        </>
      )}

      <input className="input" style={{ padding: '6px 8px', fontSize: 12.5, marginBottom: 6, width: '100%' }}
             placeholder={mode === 'support' ? 'Was hast du gemacht? (Pflicht)' : isMeeting ? 'Meeting-Inhalt (Pflicht)' : 'Was hast du gemacht? (optional)'}
             value={desc} onChange={(e) => setDesc(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Enter' && canSave) save(); }}/>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="time" className="input" style={{ padding: '4px 8px', fontSize: 12, flex: 1 }} value={from} onChange={(e) => setFrom(e.target.value)}/>
        <span className="tiny muted">bis</span>
        <input type="time" className="input" style={{ padding: '4px 8px', fontSize: 12, flex: 1 }} value={to} onChange={(e) => setTo(e.target.value)}/>
        <button className="btn btn-primary" onClick={save} disabled={!canSave}
                style={{ padding: '6px 12px', opacity: canSave ? 1 : 0.45, whiteSpace: 'nowrap' }}>
          <IconPlus size={13}/> Eintragen
        </button>
      </div>
      {!timeValid && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 6 }}>Endzeit muss nach Startzeit liegen.</div>}
      {err && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

function MissingTimesModal({ days }) {
  const { timeEntries, currentProfile, projectById, addAbsence, deleteTimeEntry } = useData();

  // Snapshot the queue once at mount and step through it. The live `days` prop
  // only shrinks as we resolve, so the modal stays mounted until the last day
  // is cleared — at which point the parent stops rendering it.
  const [queue] = useState(() => [...days]);
  const [idx, setIdx] = useState(0);
  const [absChoice, setAbsChoice] = useState(false);
  const day = queue[idx];

  const scrollRef = useRef(null);
  useEffect(() => { scrollRef.current?.scrollTo?.({ top: 0 }); setAbsChoice(false); }, [idx]);

  // Block Escape — this dialog is mandatory.
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') e.stopPropagation(); };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, []);

  // Live entries for the day currently being worked.
  const dayEntries = useMemo(
    () => timeEntries
      .filter(te => te.user_id === currentProfile.id && te.entry_date === day)
      .sort((a, b) => a.from_time.localeCompare(b.from_time)),
    [timeEntries, currentProfile.id, day]
  );
  const dayTotal = dayEntries.reduce((acc, e) => acc + durationMin(e.from_time, e.to_time), 0);
  const resolved = dayEntries.length > 0;

  const isLast = idx >= queue.length - 1;

  async function markAbsent(kind) {
    try {
      await addAbsence({
        user_id: currentProfile.id,
        start_date: day,
        end_date: day,
        kind,
        note: 'Nachträglich beim Zeiten-Nachtragen erfasst',
      });
      // Day is now covered → it drops out of the due list. Advance.
      next();
    } catch (e) {
      alert(e.message || 'Konnte Abwesenheit nicht speichern');
    }
  }

  function next() {
    if (!isLast) setIdx(i => i + 1);
    // On the last day, resolving it empties the live due list and the parent
    // unmounts this modal automatically — no explicit close needed.
  }

  if (!day) return null;

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.stopPropagation()}>
      <div className="modal" style={{ width: 560, display: 'flex', flexDirection: 'column', maxHeight: '88vh' }}>
        <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid var(--stroke)' }}>
          <div className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--crit)' }}>
            <IconClock size={12}/> Zeiterfassung — Pflicht
            {queue.length > 1 && (
              <span className="muted" style={{ fontWeight: 400 }}> · Tag {idx + 1}/{queue.length}</span>
            )}
          </div>
          <h2 className="serif" style={{ fontSize: 21, fontWeight: 500, margin: '6px 0 0', letterSpacing: '-0.01em' }}>
            {formatMissingDay(day)}
          </h2>
          <div className="tiny muted" style={{ marginTop: 4 }}>
            An diesem Werktag fehlt deine Zeiterfassung. Trage mindestens einen Eintrag nach
            — oder markiere den Tag als Urlaub/Krank.
          </div>
        </div>

        <div ref={scrollRef} style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          {/* Bereits nachgetragene Einträge */}
          {dayEntries.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div className="eyebrow" style={{ marginBottom: 8 }}>Nachgetragen · {fmtMin(dayTotal)}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {dayEntries.map(e => {
                  const kind = e.kind || (e.project_id ? 'project' : 'support');
                  const label = e.description
                    || projectById(e.project_id)?.title
                    || (kind === 'support' ? 'Begleitaufgabe' : kind === 'meeting' ? 'Meeting' : 'Eintrag');
                  return (
                    <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: 'var(--ok-soft)', border: '1px solid var(--ok)', borderRadius: 8 }}>
                      <span className="mono tiny" style={{ color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{e.from_time.slice(0, 5)}–{e.to_time.slice(0, 5)}</span>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                      <span className="mono tiny" style={{ color: 'var(--ink-2)' }}>{fmtMin(durationMin(e.from_time, e.to_time))}</span>
                      <button className="icon-btn" title="Eintrag löschen"
                              onClick={() => deleteTimeEntry(e.id).catch(() => {})}><IconX size={12}/></button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <DayEntryForm day={day} onAdded={() => {}} />

          {/* Abwesenheit nachtragen */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--stroke)' }}>
            {!absChoice ? (
              <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setAbsChoice(true)}>
                Ich habe an diesem Tag nicht gearbeitet
              </button>
            ) : (
              <div>
                <div className="tiny muted" style={{ marginBottom: 8 }}>Diesen Tag stattdessen markieren als:</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={() => markAbsent('vacation')}>🏖️ Urlaub</button>
                  <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={() => markAbsent('sick')}>🤒 Krank</button>
                  <button className="btn btn-ghost" onClick={() => setAbsChoice(false)}>Abbrechen</button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--stroke)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          {resolved ? (
            <span className="tiny" style={{ color: 'var(--ok)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconCheck size={13}/> {dayEntries.length} {dayEntries.length === 1 ? 'Eintrag' : 'Einträge'} · {fmtMin(dayTotal)}
            </span>
          ) : (
            <span className="tiny" style={{ color: 'var(--crit)' }}>Mindestens ein Eintrag erforderlich.</span>
          )}
          <button className="btn btn-primary" onClick={next} disabled={!resolved}
                  style={{ opacity: resolved ? 1 : 0.45 }}>
            {isLast ? 'Fertig' : 'Weiter →'}
          </button>
        </div>
      </div>
    </div>
  );
}

export { MissingTimesModal };
